/* The HTTP client.
 *
 * Every call goes through here so three things are true everywhere: cookies are
 * sent, an error carries a sentence the UI can show as written, and an expired
 * access token refreshes itself once rather than bouncing someone to the sign-in
 * screen mid-task.
 *
 * The session lives in httpOnly cookies, so there is no token to attach and
 * nothing here reads one. `credentials: 'include'` is the whole of it. */
import type { ApiErrorBody } from '@oneshot2pac/shared';

const BASE = '/api';

/** A failed request. `message` is the server's sentence — what happened and how
 *  to fix it — so callers show it rather than composing their own. */
export class ApiError extends Error {
  readonly status: number;
  readonly issues: Record<string, string>;

  constructor(status: number, message: string, issues: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.issues = issues;
  }

  /** True when the caller simply isn't signed in — the app shows the sign-in
   *  screen for this, never an error. */
  get isSignedOut(): boolean {
    return this.status === 401;
  }
}

const OFFLINE = 'Could not reach the server. Check the wifi and try again — nothing has been lost.';

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set on the refresh call itself, so a failed refresh can't recurse. */
  noRetry?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, noRetry = false } = options;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A network failure is not a signed-out state. Saying so would sign someone
    // out every time the shop wifi drops, which is the fastest way to lose them.
    throw new ApiError(0, OFFLINE);
  }

  // The access token lasts 15 minutes and the refresh token 30 days, so a 401
  // on a normal request usually just means "mint a new access token".
  if (response.status === 401 && !noRetry && path !== '/auth/refresh') {
    const refreshed = await refreshSession();
    if (refreshed) return request<T>(path, { ...options, noRetry: true });
  }

  if (response.status === 204) return undefined as T;

  const payload = await readBody(response);

  if (!response.ok) {
    const error = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      error?.error ?? `The server returned ${response.status}.`,
      error?.issues ?? {},
    );
  }

  return payload as T;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // An HTML error page where JSON was expected — usually a proxy or a route
    // that doesn't exist. Say that, rather than "Unexpected token <".
    return { error: `The server sent something unexpected (${response.status}).` };
  }
}

/** Rotate the session. Returns false rather than throwing: the caller's next
 *  step is to show the sign-in screen, not to report an error. */
async function refreshSession(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
